import {
  BrowserMessageType,
  CommonMessageType,
  exchangeElectronMessage,
  sendElectronMessage,
} from 'electron-communication-lib/BrowserForApp';
import {createContext, KeyboardEvent} from 'react';
import {SHOULD_STORE_PASSWORD} from 'webapp-lib/utils';

import communicationBackend from '@lexian/api/communication-api';
import documentsBackend from '@lexian/api/documents-api';
import {Casus, InvoiceDocumentInResponse} from '@lexian/api/invoicing-api';
import {DocumentUploadStore} from '_/store/DocumentUpload.store';

import {TabBarStore} from '../store/tabBar.store';

export {unexpectedError} from './store';

export {copyToClipboard} from './copyToClipboard';

export {AllowedLocaleDateFormats} from './allowedLocaleDateFormats';

export {Filter, DateRangeFilter, useFilterValues} from './filter';

export type {Awaited, LoggedInUserInformation} from './types';
export {State} from './types';
export * from './routing.utils';

export const TabBarContext = createContext<TabBarStore>(null!);
export const DocumentUploadContext = createContext<DocumentUploadStore>(null!);

export const sum = (number1: number, number2: number): number =>
  number1 + number2;

export const convertDecimalToPercent = (
  decimalValue: number | string,
  appendPercentageSymbol = false
): string => {
  const numberValue =
    typeof decimalValue === 'number' ? decimalValue : parseFloat(decimalValue);

  return (numberValue * 100).toFixed(2) + (appendPercentageSymbol ? ' %' : '');
};

export const convertPercentToDecimal = (
  percentValue: number | string
): string => {
  const numberValue =
    typeof percentValue === 'number' ? percentValue : parseFloat(percentValue);

  return (numberValue / 100).toFixed(4);
};

export const BLOCKING_EVENTS = [
  'doubleClick',
  'touchStart',
  'touchMove',
  'touchCancel',
  'touchEnd',
  'keyPress',
  'keyDown',
  'keyUp',
  'mouseDown',
  'mouseEnter',
  'mouseLeave',
  'mouseMove',
  'mouseOut',
  'wheel',
  'contextMenu',
  'drag',
];

export const onEnterPressed =
  (callback: (...items: unknown[]) => void) =>
  (evt: KeyboardEvent): void => {
    if (evt.key === 'Enter') {
      callback();
    }
  };

/**
 * Helper function to filter out null and undefined values from arrays.
 *
 * Ensures that the type is not null and not undefined after filtering.
 */
export const notUndefined = <T>(value: T | null | undefined): value is T =>
  value !== null && value !== undefined;

/**
 * Check if the current browser this application is running in is electron
 * copied and modified from https://raw.githubusercontent.com/cheton/is-electron/master/index.js
 * https://github.com/electron/electron/issues/2288
 */
export const isElectron = (): boolean =>
  (typeof process !== 'undefined' &&
    typeof process.versions === 'object' &&
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    Boolean(process.versions.electron)) ||
  (typeof window !== 'undefined' &&
    typeof window.process === 'object' &&
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.process.type === 'renderer') ||
  (navigator &&
    typeof navigator.userAgent === 'string' &&
    navigator.userAgent.indexOf('Electron') >= 0);

/**
 * Opens a document, see {@link openDocument}.
 *
 * Ensures that documents of readonly casus are opened in readonly mode.
 */
export const openInvoiceDocument = (
  casus: Casus,
  invoiceDocument: InvoiceDocumentInResponse
): void => {
  openDocument(
    {
      id: invoiceDocument.documentId,
      casusId: casus.id,
      displayName: invoiceDocument.recipientDisplayName,
    },
    {readonly: casus.readonly}
  );
};

/**
 * Opens a document on the local machine if used with electron but not with the web browser.
 *
 * @param document object that has the necessary properties to locate the document
 * @param options object to set if the opened document should be read-only
 */
export const openDocument = (
  document: {
    casusId: string;
    id: string;
    fileName?: string;
    displayName?: string;
  },
  options: {readonly: boolean} = {readonly: false}
): void => {
  if (options.readonly) {
    const {casusId, id, ...params} = document;
    const remotePath = documentsBackend.downloadURL({
      casusId,
      documentId: id,
    });
    openFileReadOnly({remotePath, handle: id, ...params});
    return;
  }
  sendElectronMessage({
    payload: {
      casusId: document.casusId,
      documentId: document.id,
      fileName: document.fileName ? document.fileName : document.displayName,
    },
    type: BrowserMessageType.OPEN_FILE,
  });
};

/**
 * Opens a file as read-only on the local machine if used with electron but not with the web browser.
 *
 * @param document object that has the necessary properties to locate the document
 */
export const openFileReadOnly = (document: {
  remotePath: string;
  handle: string;
  fileName?: string;
  displayName?: string;
  id?: string;
}): void => {
  sendElectronMessage({
    payload: {
      remotePath: document.remotePath,
      handle: document.handle,
      fileId: document.id,
      fileName: document.fileName ? document.fileName : document.displayName,
    },
    type: BrowserMessageType.OPEN_FILE_READ_ONLY,
  });
};

/** Deletes a key from an object and returns said object */
export const omitObjectProperty = (
  obj: Record<string, string>,
  key: string
): Record<string, string> => {
  const {[key]: _, ...rest} = obj;
  return rest;
};

/**
 * Opens an email on the local machine if used with electron but not with the web browser.
 *
 * @param emailId string that represents the id of an email on the server
 */
export const openEmail = (emailId: string): void => {
  openFileReadOnly({
    remotePath: communicationBackend.downloadEmailURL({
      emailId,
    }),
    handle: emailId,
    id: emailId,
  });
};

/** deletes the login credentials, should be used when logging out */
export const deleteLoginCredentials = (username: string): Promise<void> => {
  localStorage.removeItem(SHOULD_STORE_PASSWORD);
  if (isElectron()) {
    return new Promise((resolve) => {
      exchangeElectronMessage(
        {
          type: CommonMessageType.DELETE_LOGIN_CREDENTIALS,
          payload: {username},
        },
        () => resolve()
      );
    });
  }
  return Promise.resolve();
};

export const clearCookies = (): void => {
  if (isElectron()) {
    sendElectronMessage({
      type: CommonMessageType.CLEAR_ELECTRON_COOKIES,
      payload: null,
    });
  }
};
